import type { InstrumentSetupResponse } from "../types/instrumentSetup";
import {
  accountNeedsInstrumentSetup,
  findInstrumentMarketDataRow,
  formatInstrumentSetupProgressDescription,
  instrumentChartsBlocked,
  instrumentNeedsSetup,
  instrumentSetupRowNeedsConfirmation,
  setupProgress,
} from "./instrumentMarketDataReadiness";
import { reportTypeRequiresInstrumentSetup } from "./reportTypeRules";

export {
  accountNeedsInstrumentSetup,
  formatInstrumentSetupProgressDescription,
  instrumentChartsBlocked,
  instrumentNeedsSetup,
  instrumentSetupRowNeedsConfirmation,
  setupProgress,
} from "./instrumentMarketDataReadiness";

/** @deprecated Display/debug only — do not use for gating. */
export function instrumentSetupHasPendingRows(
  data: Pick<InstrumentSetupResponse, "pending_count" | "pending"> | null | undefined
): boolean {
  if (!data) return false;
  return Number(data.pending_count ?? 0) > 0 || (data.pending?.length ?? 0) > 0;
}

/** Any row still needs instrument setup (Layer A). */
export function instrumentSetupHasBlockingRows(
  data: InstrumentSetupResponse | null | undefined
): boolean {
  return accountNeedsInstrumentSetup(data);
}

/** Per-symbol chart readiness — setup done and lane available (Layers A + B). */
export function isSymbolReadyInInstrumentSetup(
  data: InstrumentSetupResponse | null | undefined,
  symbol: string | null | undefined
): boolean {
  const row = findInstrumentMarketDataRow(data, symbol);
  if (!row) return false;
  return !instrumentChartsBlocked(row);
}

/**
 * Whether charts should stay behind the instrument-setup wall.
 * Account-wide: any symbol needs setup (Layer A only).
 * Per-symbol: setup incomplete or lane unavailable (Layers A + B).
 */
export function shouldBlockInstrumentSetupForCharts(input: {
  setupData: InstrumentSetupResponse | null | undefined;
  symbol?: string | null;
}): boolean {
  const { setupData, symbol } = input;
  if (!setupData) return true;
  if (symbol?.trim()) {
    const row = findInstrumentMarketDataRow(setupData, symbol);
    if (row) return instrumentNeedsSetup(row);
    return accountNeedsInstrumentSetup(setupData);
  }
  return accountNeedsInstrumentSetup(setupData);
}

/** Qualified registry symbol on report snapshot — simulator bypass per BE §10. */
export function reportHasQualifiedOhlcListing(
  report: { meta?: Record<string, unknown> } | null | undefined
): boolean {
  const listing = report?.meta?.ohlc_listing;
  return typeof listing === "string" && listing.trim().length > 0;
}

export function isInstrumentSetupPending(
  flag: boolean | null | undefined
): boolean {
  return flag === true;
}

export function shouldBlockSimulator(input: {
  instrumentSetupPending: boolean;
  variant: "standalone" | "report";
  report?: { meta?: Record<string, unknown>; report_type?: string } | null;
}): boolean {
  if (!input.instrumentSetupPending) return false;
  if (input.variant === "standalone") return true;
  if (reportHasQualifiedOhlcListing(input.report)) return false;
  return reportTypeRequiresInstrumentSetup(input.report?.report_type);
}

export function sortInstrumentSetupPending<T extends { trade_count?: number; symbol: string }>(
  rows: T[]
): T[] {
  return [...rows].sort((a, b) => {
    const diff = (b.trade_count ?? 0) - (a.trade_count ?? 0);
    if (diff !== 0) return diff;
    return a.symbol.localeCompare(b.symbol);
  });
}

/** @deprecated Use setupProgress — kept for import stability during migration. */
export function instrumentSetupProgressCounts(
  data: InstrumentSetupResponse | null | undefined
): { configured: number; needsConfirmation: number; total: number } {
  const progress = setupProgress(data);
  return {
    configured: progress.done,
    needsConfirmation: progress.needsConfirmation,
    total: progress.total,
  };
}

/** Pending rows that still need setup — mirrors accountNeedsInstrumentSetup row scan. */
export function instrumentSetupHasBlockingPendingRows(
  data: Pick<InstrumentSetupResponse, "pending"> | null | undefined
): boolean {
  if (!data) return false;
  return (data.pending ?? []).some((row) => instrumentNeedsSetup(row));
}

/** Resolved rows that still need setup. */
export function instrumentSetupHasBlockingResolvedRows(
  data: Pick<InstrumentSetupResponse, "resolved"> | null | undefined
): boolean {
  if (!data) return false;
  return (data.resolved ?? []).some((row) => instrumentNeedsSetup(row));
}
